// Analytics Service para tracking de eventos en páginas públicas
interface SessionData {
  sessionId: string
  visitorId: string
  agentId: string
  startTime: number
}

interface PageViewData {
  sessionId: string
  pageUrl: string
  pageTitle: string
  pageType: string
  propertyId?: string
}

interface ChatEventData {
  sessionId: string
  eventType: 'chat_opened' | 'message_sent' | 'data_captured' | 'chat_closed'
  messageCount?: number
  userMessage?: string
  dataCaptured?: any
  leadScore?: number
  leadTemperature?: string
}

interface PropertyInteractionData {
  sessionId: string
  propertyId: string
  interactionType: 'view' | 'image_click' | 'contact_click' | 'share'
  imageIndex?: number
}

class AnalyticsService {
  private sessionData: SessionData | null = null
  private pageStartTime: number = 0
  private currentPageOrder: number = 0
  private maxScrollPercent: number = 0
  private interactionCount: number = 0
  private agentSubdomain: string = ''

  // Inicializar tracking para un agente específico
  init(agentSubdomain: string) {
    this.agentSubdomain = agentSubdomain
    this.initSession()
    this.setupPageTracking()
    this.setupScrollTracking()
    this.setupUnloadTracking()
  }

  // Inicializar o recuperar sesión
  private async initSession() {
    try {
      // Generar IDs únicos
      let visitorId = localStorage.getItem('ia_visitor_id')
      if (!visitorId) {
        visitorId = this.generateId()
        localStorage.setItem('ia_visitor_id', visitorId)
      }

      const sessionId = this.generateId()

      // Obtener información del agente
      const agentResponse = await fetch(`/api/agents/subdomain/${this.agentSubdomain}`)
      if (!agentResponse.ok) return

      const agent = await agentResponse.json()

      // Detectar información del dispositivo/ubicación
      const deviceInfo = this.getDeviceInfo()
      const geoInfo = await this.getGeoInfo()

      this.sessionData = {
        sessionId,
        visitorId,
        agentId: agent.id,
        startTime: Date.now()
      }

      // Enviar datos de sesión al backend
      await this.sendToBackend('/api/analytics/session', {
        session_id: sessionId,
        visitor_id: visitorId,
        agent_id: agent.id,
        template_name: agent.template || 'elegante',
        ...deviceInfo,
        ...geoInfo,
        referrer_url: document.referrer,
        referrer_domain: document.referrer ? new URL(document.referrer).hostname : null,
        utm_source: this.getURLParam('utm_source'),
        utm_medium: this.getURLParam('utm_medium'),
        utm_campaign: this.getURLParam('utm_campaign')
      })

    } catch (error) {
      console.warn('Analytics session init failed:', error)
    }
  }

  // Tracking de páginas vistas
  trackPageView(pageType: string = 'home', propertyId?: string) {
    if (!this.sessionData) return

    this.currentPageOrder++
    this.pageStartTime = Date.now()
    this.maxScrollPercent = 0
    this.interactionCount = 0

    const pageData: PageViewData = {
      sessionId: this.sessionData.sessionId,
      pageUrl: window.location.href,
      pageTitle: document.title,
      pageType,
      propertyId
    }

    this.sendToBackend('/api/analytics/pageview', {
      session_id: pageData.sessionId,
      agent_id: this.sessionData.agentId,
      page_url: pageData.pageUrl,
      page_title: pageData.pageTitle,
      page_type: pageData.pageType,
      page_order: this.currentPageOrder,
      property_id: propertyId
    }).catch(console.warn)
  }

  // Tracking de eventos de chat
  trackChatEvent(eventType: ChatEventData['eventType'], data?: Partial<ChatEventData>) {
    if (!this.sessionData) return

    const eventData = {
      session_id: this.sessionData.sessionId,
      agent_id: this.sessionData.agentId,
      event_type: eventType,
      message_count: data?.messageCount,
      user_message: data?.userMessage,
      data_captured: data?.dataCaptured,
      lead_score: data?.leadScore,
      lead_temperature: data?.leadTemperature
    }

    this.sendToBackend('/api/analytics/chat-event', eventData).catch(console.warn)

    // Actualizar flags de sesión según evento
    if (eventType === 'chat_opened') {
      this.updateSessionFlag('chat_initiated', true)
    } else if (eventType === 'data_captured') {
      this.updateSessionFlag('contact_captured', true)
      this.updateSessionFlag('lead_generated', true)
    }
  }

  // Tracking de interacciones con propiedades
  trackPropertyInteraction(propertyId: string, interactionType: PropertyInteractionData['interactionType'], imageIndex?: number) {
    if (!this.sessionData) return

    this.interactionCount++

    this.sendToBackend('/api/analytics/property-interaction', {
      session_id: this.sessionData.sessionId,
      agent_id: this.sessionData.agentId,
      property_id: propertyId,
      interaction_type: interactionType,
      image_index: imageIndex,
      time_spent_seconds: Math.round((Date.now() - this.pageStartTime) / 1000)
    }).catch(console.warn)
  }

  // Configurar tracking de scroll
  private setupScrollTracking() {
    let scrollTimeout: NodeJS.Timeout

    const trackScroll = () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        const scrollPercent = Math.round(
          (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
        )
        this.maxScrollPercent = Math.max(this.maxScrollPercent, scrollPercent)
      }, 250)
    }

    window.addEventListener('scroll', trackScroll, { passive: true })
  }

  // Configurar tracking de página
  private setupPageTracking() {
    // Click tracking
    document.addEventListener('click', () => {
      this.interactionCount++
    }, { passive: true })
  }

  // Configurar tracking al salir de página
  private setupUnloadTracking() {
    const updatePageTime = () => {
      if (!this.sessionData || this.pageStartTime === 0) return

      const timeOnPage = Math.round((Date.now() - this.pageStartTime) / 1000)

      // Usar sendBeacon para envío al cerrar página
      navigator.sendBeacon('/api/analytics/page-time', JSON.stringify({
        session_id: this.sessionData.sessionId,
        time_on_page_seconds: timeOnPage,
        max_scroll_percent: this.maxScrollPercent,
        interactions_count: this.interactionCount
      }))
    }

    window.addEventListener('beforeunload', updatePageTime)
    window.addEventListener('pagehide', updatePageTime)
  }

  // Actualizar flags de sesión
  private async updateSessionFlag(flag: string, value: boolean) {
    if (!this.sessionData) return

    try {
      await this.sendToBackend('/api/analytics/session-flag', {
        session_id: this.sessionData.sessionId,
        [flag]: value
      })
    } catch (error) {
      console.warn('Failed to update session flag:', error)
    }
  }

  // Obtener información del dispositivo
  private getDeviceInfo() {
    const userAgent = navigator.userAgent
    
    let deviceType = 'desktop'
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
      deviceType = /iPad/.test(userAgent) ? 'tablet' : 'mobile'
    }

    let browser = 'unknown'
    if (userAgent.includes('Chrome')) browser = 'Chrome'
    else if (userAgent.includes('Firefox')) browser = 'Firefox'
    else if (userAgent.includes('Safari')) browser = 'Safari'
    else if (userAgent.includes('Edge')) browser = 'Edge'

    return {
      device_type: deviceType,
      browser,
      browser_version: this.getBrowserVersion(userAgent),
      os: this.getOS(userAgent),
      screen_resolution: `${screen.width}x${screen.height}`
    }
  }

  // Obtener información geográfica (simulada - en producción usar servicio real)
  private async getGeoInfo() {
    try {
      // En producción, usar un servicio como ipapi.co o similar
      const response = await fetch('https://ipapi.co/json/')
      const data = await response.json()
      
      return {
        country: data.country_code,
        region: data.region,
        city: data.city,
        ip_address: data.ip
      }
    } catch (error) {
      return {
        country: null,
        region: null,
        city: null,
        ip_address: null
      }
    }
  }

  // Utilidades
  private generateId(): string {
    return Math.random().toString(36).substr(2, 9) + Date.now().toString(36)
  }

  private getURLParam(param: string): string | null {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get(param)
  }

  private getBrowserVersion(userAgent: string): string {
    const match = userAgent.match(/(?:Chrome|Firefox|Safari|Edge)\/([0-9.]+)/)
    return match ? match[1].split('.')[0] : 'unknown'
  }

  private getOS(userAgent: string): string {
    if (userAgent.includes('Windows')) return 'Windows'
    if (userAgent.includes('Mac')) return 'macOS'
    if (userAgent.includes('Linux')) return 'Linux'
    if (userAgent.includes('Android')) return 'Android'
    if (userAgent.includes('iOS')) return 'iOS'
    return 'unknown'
  }

  // Enviar datos al backend
  private async sendToBackend(endpoint: string, data: any) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      throw new Error(`Analytics request failed: ${response.status}`)
    }

    return response.json()
  }
}

// Instancia global
export const analytics = new AnalyticsService()
export default analytics
